<template>
  <div>
    <h2 class="title">{{ modeTitle }}</h2>
    <el-form
      ref="form"
      :model="form"
      :rules="loginRules"
      labelPosition="top"
      label-width="80px"
      :hide-required-asterisk="true"
    >
      <!-- ── 登入 & 忘記密碼步驟一：顯示 email 欄位 ── -->
      <el-form-item prop="email" v-if="mode !== 'forgot-2'">
        <label class="label" slot="label">{{ $t('login.email') }}</label>
        <el-input
          placeholder="Email"
          type="email"
          size="large"
          v-model="form.email"
          @keyup.enter.native="mode === 'login' ? submitLoginForm() : submitForgot1()"
        ></el-input>
      </el-form-item>

      <!-- ── 登入：密碼欄位 ── -->
      <el-form-item prop="password" v-if="mode === 'login'">
        <label class="label" slot="label">{{ $t('login.psd') }}</label>
        <el-input
          v-model="form.password"
          placeholder="Password"
          type="password"
          :minlength="6"
          @keyup.enter.native="submitLoginForm"
        ></el-input>
      </el-form-item>

      <!-- ── 登入：按鈕 + 忘記密碼連結 ── -->
      <template v-if="mode === 'login'">
        <a
          class="button mb-16 is-info is-primary is-fullwidth is-medium"
          @click="submitLoginForm"
          :class="{ 'is-loading': isValidating }"
          >{{ $t('login.title') }}</a
        >
        <p style="text-align: right; margin-bottom: 12px;">
          <a class="forgot-link" @click="switchToForgot">{{ $t('login.forgot') }}</a>
        </p>
      </template>

      <!-- ── 忘記密碼步驟一：發送驗證碼 + 返回 ── -->
      <template v-else-if="mode === 'forgot-1'">
        <a
          class="button mb-16 is-info is-primary is-fullwidth is-medium"
          @click="submitForgot1"
          :class="{ 'is-loading': isValidating, 'is-disabled-link': isCoolingDown }"
          >{{ isCoolingDown ? `發送驗證碼（${resendCooldown}s）` : '發送驗證碼' }}</a
        >
        <p style="text-align: center; margin-bottom: 12px;">
          <a class="forgot-link" @click="switchToLogin">返回登入</a>
        </p>
      </template>

      <!-- ── 忘記密碼步驟二：OTP + 新密碼 ── -->
      <template v-else>
        <el-form-item>
          <label class="label" slot="label">{{ $t('login.email') }}</label>
          <el-input :value="form.email" disabled size="large"></el-input>
        </el-form-item>
        <el-form-item prop="otp">
          <label class="label" slot="label">驗證碼</label>
          <el-input
            v-model="form.otp"
            placeholder="請輸入驗證碼（有效期 15 分鐘）"
            maxlength="6"
            @keyup.enter.native="submitForgot2"
          ></el-input>
        </el-form-item>
        <el-form-item prop="newpassword">
          <label class="label" slot="label">{{ $t('login.npsd') }}</label>
          <el-input
            v-model="form.newpassword"
            placeholder="Password"
            type="password"
            @keyup.enter.native="submitForgot2"
          ></el-input>
          <password-checklist :password="form.newpassword" />
        </el-form-item>
        <el-form-item prop="psdCheck">
          <label class="label" slot="label">{{ $t('login.psdCheck') }}</label>
          <el-input
            v-model="form.psdCheck"
            placeholder="Password"
            type="password"
            @keyup.enter.native="submitForgot2"
          ></el-input>
        </el-form-item>
        <a
          class="button mb-16 is-info is-primary is-fullwidth is-medium"
          @click="submitForgot2"
          :class="{ 'is-loading': isValidating }"
          >{{ $t('login.reset') }}</a
        >
        <a
          class="button is-fullwidth is-medium"
          @click="sendForgotOtp"
          :class="{ 'is-loading': isValidating, 'is-disabled-link': isCoolingDown }"
          >{{ isCoolingDown ? `重新發送驗證碼（${resendCooldown}s）` : '重新發送驗證碼' }}</a
        >
      </template>

      <span class="tips has-text-grey">Please contact geosupply.tw@gmail.com for demo pass</span>
    </el-form>
  </div>
</template>

<script>
import { clientVersion } from '../../constants/env';
import PasswordChecklist from '@/components/common/PasswordChecklist.vue';
import {
  readOtpCooldown,
  writeOtpCooldown,
  OTP_RESEND_COOLDOWN_SECONDS,
} from '@/lib/base/otpCooldown';

export default {
  name: 'LoginPage',
  components: { PasswordChecklist },
  data() {
    const validateLoginPass = (rule, value, callback) => {
      if (value.length < 6) {
        callback(new Error(this.$t('login.psdValidate')));
      } else {
        callback();
      }
    };
    const validateNewPass = (rule, value, callback) => {
      if (!value) { callback(new Error('請輸入新密碼')); return; }
      if (value.length < 12) { callback(new Error('密碼至少需要 12 個字元')); return; }
      if (!/[A-Z]/.test(value)) { callback(new Error('密碼需包含至少一個大寫字母（A–Z）')); return; }
      if (!/[a-z]/.test(value)) { callback(new Error('密碼需包含至少一個小寫字母（a–z）')); return; }
      if (!/[0-9]/.test(value)) { callback(new Error('密碼需包含至少一個數字（0–9）')); return; }
      if (!/[^A-Za-z0-9]/.test(value)) { callback(new Error('密碼需包含至少一個特殊字元（!@#$%^&* 等）')); return; }
      callback();
    };
    const validatePsdCheck = (rule, value, callback) => {
      if (value !== this.form.newpassword) {
        callback(new Error(this.$t('login.psdCheckValidate')));
      } else {
        callback();
      }
    };
    return {
      mode: 'login', // 'login' | 'forgot-1' | 'forgot-2'
      form: {
        email: null,
        password: null,
        otp: null,
        newpassword: null,
        psdCheck: null,
      },
      loginRules: {
        email: [
          { required: true, message: '請輸入郵箱', trigger: 'blur' },
          { type: 'email', message: '請輸入正確的郵箱', trigger: 'blur' },
        ],
        password: [{ required: true, trigger: 'blur', validator: validateLoginPass }],
        otp: [{ required: true, message: '請輸入驗證碼', trigger: 'blur' }],
        newpassword: [{ required: true, trigger: 'blur', validator: validateNewPass }],
        psdCheck: [{ required: true, trigger: 'blur', validator: validatePsdCheck }],
      },
      isValidating: false,
      project: null,
      resendCooldown: 0,
      cooldownEmail: null,
      cooldownTimer: null,
    };
  },
  computed: {
    isAuthenticated() {
      return this.$store.getters['user/isAuthenticated'];
    },
    selectedProject() {
      return this.$store.getters['project/selectedProject'];
    },
    occRole() {
      return this.userRole === 'occ';
    },
    userRole() {
      return this.$store.getters['user/details'].roles;
    },
    modeTitle() {
      if (this.mode === 'forgot-1') return '忘記密碼';
      if (this.mode === 'forgot-2') return '重設密碼';
      return this.$t('login.title');
    },
    isCoolingDown() {
      return (
        this.resendCooldown > 0 &&
        !!this.cooldownEmail &&
        !!this.form.email &&
        this.cooldownEmail.trim().toLowerCase() === this.form.email.trim().toLowerCase()
      );
    },
  },
  mounted() {
    if (this.isAuthenticated) {
      this.$router.push(`/${this.$store.state.locale}/data`);
    }
    this.restoreCooldown();
  },
  beforeDestroy() {
    clearInterval(this.cooldownTimer);
  },
  methods: {
    restoreCooldown() {
      const cooldown = readOtpCooldown();
      if (!cooldown) return;
      this.cooldownEmail = cooldown.email;
      this.startCooldownTimer(cooldown.remain);
    },
    startCooldownTimer(seconds) {
      clearInterval(this.cooldownTimer);
      this.resendCooldown = seconds;
      this.cooldownTimer = setInterval(() => {
        this.resendCooldown -= 1;
        if (this.resendCooldown <= 0) {
          clearInterval(this.cooldownTimer);
          this.cooldownTimer = null;
          this.cooldownEmail = null;
        }
      }, 1000);
    },
    switchToForgot() {
      this.mode = 'forgot-1';
      this.$refs.form.clearValidate();
    },
    switchToLogin() {
      this.mode = 'login';
      this.form.otp = null;
      this.form.newpassword = null;
      this.form.psdCheck = null;
      this.$refs.form.clearValidate();
    },
    submitLoginForm() {
      this.$refs.form.validate(valid => {
        if (valid) {
          this.loginUser();
        } else {
          return false;
        }
      });
    },
    submitForgot1() {
      this.$refs.form.validate(valid => {
        if (valid) this.sendForgotOtp();
      });
    },
    submitForgot2() {
      this.$refs.form.validate(valid => {
        if (valid) this.resetForgotPassword();
      });
    },
    async sendForgotOtp() {
      if (this.isValidating || this.isCoolingDown) return;
      this.isValidating = true;
      this.form.otp = null;
      try {
        await this.$axios({
          category: 'user',
          apiName: 'requestPasswordReset',
          method: 'post',
          data: { email: this.form.email },
        });
        this.$notifySuccess('您將收到一封含有驗證碼的信件');
        this.mode = 'forgot-2';
      } catch (error) {
        // 後端無論信箱是否存在皆回 200，此處處理非預期錯誤
        this.$notifySuccess('您將收到一封含有驗證碼的信件');
        this.mode = 'forgot-2';
      } finally {
        this.isValidating = false;
        writeOtpCooldown(this.form.email, OTP_RESEND_COOLDOWN_SECONDS);
        this.cooldownEmail = this.form.email;
        this.startCooldownTimer(OTP_RESEND_COOLDOWN_SECONDS);
      }
    },
    async resetForgotPassword() {
      this.isValidating = true;
      try {
        const res = await this.$axios({
          category: 'user',
          apiName: 'resetPassword',
          method: 'put',
          data: {
            email: this.form.email,
            otp: this.form.otp,
            newPassword: this.form.newpassword,
          },
        });
        const msg = res.data?.message || '密碼已重設，請重新登入';
        this.$notifySuccess(msg);
        this.switchToLogin();
      } catch (error) {
        const status = error.response?.status;
        const data = error.response?.data;
        if (status === 400 || status === 401) {
          const rawMsg = data?.message;
          const msg = Array.isArray(rawMsg) ? rawMsg.join('、') : rawMsg;
          this.$notifyError(msg || '操作失敗，請稍後再試');
        } else {
          this.$notifyError('操作失敗，請稍後再試');
        }
      } finally {
        this.isValidating = false;
      }
    },
    getProjectList() {
      return this.$store.dispatch('project/getProjectList').catch(error => {
        throw new Error(error);
      });
    },
    async loginUser() {
      this.isValidating = true;
      return this.$store
        .dispatch('user/loginUser', this.form)
        .then(() => {
          return this.getProjectList();
        })
        .then(() => {
          this.$notifySuccess('登入成功');
          if (this.$route.query && this.$route.query.redirect) {
            console.log('redirect');
            this.$router.push(this.$route.query.redirect);
          }
          else if(this.occRole){
            console.log('occ');
            this.$router.push({ path: 'occ' });
          }
          else {
            console.log('simple');
            this.$router.push({ name: this.selectedProject.first_display });
          }
        })
        .catch(error => {
          const rawMsg = error?.response?.data?.message;
          const msg = Array.isArray(rawMsg) ? rawMsg.join('、') : rawMsg;
          if(msg === '密碼不能與手機號相同'){
            this.$notifyInfo('提示','您使用的是初始密碼，請修改密碼');
            this.$router.push({ name: 'client',query: { form: this.form } });
          }
          else if(msg === '基於安全需求，請修改密碼'){
            this.$notifyInfo('提示','基於安全需求，我們要求3月/6月/9月/12月修改一次密碼，請修改密碼。');
            this.$router.push({ name: 'client',query: { form: this.form } });
          }
          else if(msg === '登入失敗次數過多，帳戶已被鎖定15分鐘'){
            this.$notifyError('帳戶已被鎖定15分鐘，請稍後再試');
          }
          else if(msg === '帳戶已被鎖定，請15分鐘後再試'){
            this.$notifyError('帳戶鎖定中，請15分鐘後再試');
          }
          else if(msg === '信箱或密碼錯誤'){
            const d = error?.response?.data;
            const attemptsInfo = (d?.attempts && d?.maxAttempts)
              ? `（已輸入錯誤 ${d.attempts}/${d.maxAttempts} 次）`
              : '';
            this.$notifyError(`信箱或密碼錯誤${attemptsInfo}`);
          }
          else if(msg){
            this.$notifyError(msg);
          }
          else{
            console.error(error);
            this.$notifyError('登入失敗，請稍後再試');
          }
        })
        .finally(() => {
          this.isValidating = false;
        });
    },
  },
};
</script>

<style scoped>
.forgot-link {
  color: #409eff;
  cursor: pointer;
  font-size: 13px;
}
.forgot-link:hover {
  text-decoration: underline;
}
.is-disabled-link {
  pointer-events: none;
  opacity: 0.5;
}
</style>
